import React, { useState, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import axios from 'axios';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { TRANSLATIONS } from '../../../translations';
import { BREAKPOINTS } from '../../../constants';
import { ModalOrderCard } from './subComponents/ModalOrderCard';
import { ModalDrawerResponse } from './ModalDrawerResponse';
import { ModalDrawerExitFlow } from './ModalDrawerExitFlow';
import { getRequestType } from '../../hooks/useOrderListUtils';
import { Loader } from '../common';

const modalTitle = TRANSLATIONS.BOOKING_TO_VOUCHER;
const REQUEST_LABEL = 'to_voucher';

//NotCompleted = onHold and confirmed orders
const ModalNotCompletedToVoucher = ({
  isOpen,
  setIsOpen,
  token,
  order,
  customGoBackFunc,
  setResponseModal,
  setRequestDetails,
}) => {
  if (!order || !token) return null;

  const orderId = order.id;
  const firstStep = 1;
  const requestType = getRequestType(order.status, REQUEST_LABEL);
  const isDeviceSmallerThanMblLandscape = !useMatchesMinWidth(
    BREAKPOINTS.MBL_LANDSCAPE
  );

  const [step, setStep] = useState(firstStep);
  const [showModalExit, setShowModalExit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [responseModalState, setResponseModalState] = useState({
    isOpen: false,
    isSuccess: false,
    responseContent: '',
    modalTitle: '',
  });
  const actualSetResponseModal = setResponseModal || setResponseModalState;

  useEffect(() => {
    document.body.classList.toggle('noscroll', isOpen);
    return () => document.body.classList.remove('noscroll');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setShowModalExit(false);
    }
  }, [isOpen]);

  const onSubmit = async () => {
    try {
      setIsLoading(true);

      const { data } = await axios.post(
        `${endpoints.core}/order-customer-request/store`,
        {
          order_id: orderId,
          type: requestType,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      let requestDetails = data && data.order ? data.order : null;
      setRequestDetails(requestDetails);
      actualSetResponseModal({
        isOpen: true,
        isSuccess: true,
        responseContent:
          TRANSLATIONS.YOUR_BOOKING_TO_VOUCHER_REQUEST_WAS_SUCCESSFUL,
        modalTitle: modalTitle,
      });
    } catch (error) {
      // console.log(error);
      actualSetResponseModal({
        isOpen: true,
        isSuccess: false,
        responseContent:
          TRANSLATIONS.UNEXPECTED_ERROR_HAS_OCCURRED_PLEASE_RETRY_LATER,
        modalTitle: modalTitle,
      });
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  const firstStepModal = (
    <ModalDrawer
      goBack={customGoBackFunc}
      isOpen={isOpen && step === 1 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        isLoading ? (
          <div className="d-flex justify-center text-center">
            <Loader loading={isLoading} />
          </div>
        ) : (
          <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
            <div className="d-flex flex-column gap-xs">
              <div
                className="typography__p3 typography__neutral text-left"
                dangerouslySetInnerHTML={{
                  __html: TRANSLATIONS.BOOKING_TO_VOUCHER_REQUEST_SUMMARY,
                }}
              />
              <ModalOrderCard order={order} toVoucher={true} />
            </div>
          </div>
        )
      }
      footer={
        <div className="d-flex gap-m pb-m pl-xl pr-xl">
          <Button
            disabled={isLoading}
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={TRANSLATIONS.CANCEL}
            onClick={() => setShowModalExit(true)}
          />
          <Button
            disabled={isLoading}
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.secondary}
            label={TRANSLATIONS.CONFIRM}
            onClick={onSubmit}
          />
        </div>
      }
    />
  );

  const responseModalDrawer = (
    <ModalDrawerResponse
      isOpen={responseModalState.isOpen}
      setIsOpen={actualSetResponseModal}
      isSuccess={responseModalState.isSuccess}
      modalTitle={responseModalState.modalTitle}
      responseContent={responseModalState.responseContent}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
    />
  );

  return (
    <>
      {responseModalDrawer}
      {firstStepModal}

      <ModalDrawerExitFlow
        isOpen={showModalExit}
        setIsOpen={setShowModalExit}
        closeAction={() => setIsOpen(false)}
        isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      />
    </>
  );
};

ModalNotCompletedToVoucher.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  order: PropTypes.object.isRequired,
  customGoBackFunc: PropTypes.func,
  setResponseModal: PropTypes.func,
};

export { ModalNotCompletedToVoucher };
